import { useState, useEffect, useCallback } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { SuperAdminAPI } from '../lib/b2bApi';
import { Bell, Send, Users, Stethoscope, Building2, Heart, Loader2, Clock, CheckCircle, XCircle } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({ baseURL: API_URL });
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('super_admin_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const TEMPLATES = {
  doctors: [
    { label: 'General Update', title: 'Update from 1Clin', body: 'We have an important update for you. Please check your dashboard.' },
    { label: 'New Feature', title: 'New Feature Available', body: 'A new feature has been added to your dashboard. Login to explore!' },
    { label: 'Maintenance Alert', title: 'Scheduled Maintenance', body: 'We will be performing maintenance on {date}. The platform may be briefly unavailable.' },
    { label: 'Custom', title: '', body: '' },
  ],
  vendors: [
    { label: 'General Update', title: 'Update from 1Clin', body: 'We have an important update for our vendor partners. Please check your dashboard.' },
    { label: 'New Orders Reminder', title: 'Pending Orders', body: 'You have pending orders that need your attention. Please review them.' },
    { label: 'Maintenance Alert', title: 'Scheduled Maintenance', body: 'We will be performing maintenance on {date}. The platform may be briefly unavailable.' },
    { label: 'Custom', title: '', body: '' },
  ],
  patients: [
    { label: 'General Update', title: 'Update from 1Clin', body: 'We have an important update for you. Please check your patient portal.' },
    { label: 'Health Tip', title: 'Health Tip', body: 'Stay hydrated and maintain a balanced diet for optimal health!' },
    { label: 'Maintenance Alert', title: 'Scheduled Maintenance', body: 'We will be performing maintenance on {date}. The platform may be briefly unavailable.' },
    { label: 'Custom', title: '', body: '' },
  ],
  all: [
    { label: 'General Announcement', title: '1Clin Announcement', body: 'We have an important announcement for all users.' },
    { label: 'Maintenance Alert', title: 'Scheduled Maintenance', body: 'We will be performing maintenance on {date}. The platform may be briefly unavailable.' },
    { label: 'Custom', title: '', body: '' },
  ],
};

const AUDIENCE_OPTIONS = [
  { value: 'doctors', label: 'Doctors', icon: Stethoscope, color: 'text-blue-600' },
  { value: 'vendors', label: 'Vendors', icon: Building2, color: 'text-emerald-600' },
  { value: 'patients', label: 'Patients', icon: Heart, color: 'text-rose-600' },
  { value: 'all', label: 'All Users', icon: Users, color: 'text-purple-600' },
];

export default function PushNotificationsPanel() {
  const [sending, setSending] = useState(false);
  const [audience, setAudience] = useState('doctors');
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [url, setUrl] = useState('/');
  const [history, setHistory] = useState([]);
  const [stats, setStats] = useState({ stats: {}, total: 0 });
  const [loadingHistory, setLoadingHistory] = useState(false);

  const fetchHistory = useCallback(async () => {
    setLoadingHistory(true);
    try {
      const [histRes, statsRes] = await Promise.all([
        api.get('/api/push-notifications/broadcast-history'),
        api.get('/api/push-notifications/subscriber-stats'),
      ]);
      setHistory(histRes.data.history || []);
      setStats(statsRes.data || { stats: {}, total: 0 });
    } catch (err) {
      console.error('Failed to fetch push history:', err);
    }
    setLoadingHistory(false);
  }, []);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  const handleTemplateSelect = (templateLabel) => {
    setSelectedTemplate(templateLabel);
    const templates = TEMPLATES[audience] || TEMPLATES.all;
    const tmpl = templates.find(t => t.label === templateLabel);
    if (tmpl) {
      setTitle(tmpl.title);
      setBody(tmpl.body);
    }
  };

  const handleAudienceChange = (val) => {
    setAudience(val);
    setSelectedTemplate('');
    setTitle('');
    setBody('');
  };

  const handleSend = async () => {
    if (!title.trim() || !body.trim()) {
      toast.error('Please fill in title and message');
      return;
    }
    setSending(true);
    try {
      const res = await api.post('/api/push-notifications/broadcast', {
        title,
        body,
        target_audience: audience,
        url: url || '/',
      });
      const data = res.data;
      toast.success(`Broadcast sent! ${data.inapp_delivered || 0} in-app notifications delivered${data.sent > 0 ? `, ${data.sent} push delivered` : ''}`);
      setTitle('');
      setBody('');
      setSelectedTemplate('');
      fetchHistory();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send broadcast');
    }
    setSending(false);
  };

  const templates = TEMPLATES[audience] || TEMPLATES.all;

  return (
    <div className="space-y-6">
      {/* Stats Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {AUDIENCE_OPTIONS.map(opt => (
          <Card key={opt.value}>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg bg-slate-100`}>
                  <opt.icon className={`h-5 w-5 ${opt.color}`} />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">{opt.label}</p>
                  <p className="text-xl font-bold" data-testid={`push-stat-${opt.value}`}>
                    {opt.value === 'all' ? stats.total : (stats.stats?.[opt.value === 'doctors' ? 'doctor' : opt.value === 'vendors' ? 'vendor' : 'patient'] || 0)}
                  </p>
                  <p className="text-xs text-muted-foreground">subscribers</p>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Compose Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bell className="w-5 h-5 text-purple-600" />
            Send Push Notification
          </CardTitle>
          <CardDescription>Broadcast push notification to app users</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Target Audience</Label>
              <Select value={audience} onValueChange={handleAudienceChange}>
                <SelectTrigger data-testid="push-audience-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {AUDIENCE_OPTIONS.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Template</Label>
              <Select value={selectedTemplate} onValueChange={handleTemplateSelect}>
                <SelectTrigger data-testid="push-template-select">
                  <SelectValue placeholder="Select a template..." />
                </SelectTrigger>
                <SelectContent>
                  {templates.map(t => (
                    <SelectItem key={t.label} value={t.label}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Title</Label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Notification title"
              data-testid="push-title-input"
            />
          </div>

          <div className="space-y-2">
            <Label>Message</Label>
            <Textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Notification message body..."
              rows={3}
              data-testid="push-body-input"
            />
          </div>

          <div className="space-y-2">
            <Label>Link URL <span className="text-xs text-muted-foreground">(optional - where to navigate on click)</span></Label>
            <Input
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="/"
              data-testid="push-url-input"
            />
          </div>

          <Button
            onClick={handleSend}
            disabled={sending || !title.trim() || !body.trim()}
            className="w-full md:w-auto bg-purple-600 hover:bg-purple-700"
            data-testid="push-send-button"
          >
            {sending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
            Send to {AUDIENCE_OPTIONS.find(o => o.value === audience)?.label || 'Users'}
          </Button>
        </CardContent>
      </Card>

      {/* History */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="w-5 h-5 text-slate-600" />
            Broadcast History
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loadingHistory ? (
            <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-purple-600" /></div>
          ) : history.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">No broadcasts sent yet</p>
          ) : (
            <div className="space-y-3">
              {history.map((item) => (
                <div key={item.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg" data-testid="push-history-item">
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-sm truncate">{item.title}</p>
                    <p className="text-xs text-muted-foreground truncate">{item.body}</p>
                    <div className="flex gap-2 mt-1">
                      <Badge variant="outline" className="text-xs">
                        {item.target_audience}
                      </Badge>
                      <span className="text-xs text-muted-foreground">
                        {new Date(item.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </div>
                  </div>
                  <div className="flex gap-3 text-xs ml-3 shrink-0">
                    <span className="flex items-center gap-1 text-green-600">
                      <CheckCircle className="h-3 w-3" /> {item.sent}
                    </span>
                    {item.failed > 0 && (
                      <span className="flex items-center gap-1 text-red-500">
                        <XCircle className="h-3 w-3" /> {item.failed}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
